import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ButtonPrimary,
    Input,
    Alert,
    Spinner,
    Icon
} from '../components/ComponentLibrary';
import { User, Phone, Calendar, Mail, Sparkles, CheckCircle2, Video, Users, Award, Clock } from 'lucide-react';
import logoImg from '../img/Untitled design (19).png';
import './FreeSessionRegistration.css';
import api from '../services/api';

export default function FreeSessionRegistration() {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        fullName: "",
        age: "",
        phone: "",
        email: "",
        preferredTime: "",
        goals: ""
    });

    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        // Validation
        if (!formData.fullName.trim() || !formData.age || !formData.phone.trim() || !formData.email.trim()) {
            setError("من فضلك املأ كل الحقول المطلوبة.");
            return;
        }

        if (formData.age < 6 || formData.age > 100) {
            setError("يرجى إدخال العمر بشكل صحيح (6-100 سنة).");
            return;
        }

        if (formData.phone.length < 10) {
            setError("يرجى إدخال رقم هاتف صحيح.");
            return;
        }

        setLoading(true);
        try {
            await api.post('/free-session/register', formData);
            setSuccess(true);

            // Redirect to success page after 3 seconds
            setTimeout(() => {
                navigate('/');
            }, 3000);
        } catch (err) {
            const errorMsg = err.response?.data?.error || "حصل خطأ أثناء التسجيل. حاول مرة أخرى.";
            const debugInfo = err.response?.data?.debug ? ` (${err.response.data.debug})` : "";
            setError(errorMsg + debugInfo);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="auth-page">
                <div className="success-container">
                    <div className="success-card">
                        <div className="success-icon-wrapper">
                            <CheckCircle2 size={80} className="success-icon" />
                        </div>
                        <h1 className="success-title">🎉 تم التسجيل بنجاح!</h1>
                        <p className="success-message">
                            شكراً لاهتمامك! سيتم التواصل معك قريباً لتحديد موعد السيشن المجانية.
                        </p>
                        <div className="success-footer">
                            <p>جاري التحويل إلى الصفحة الرئيسية...</p>
                            <Spinner size="sm" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page free-session-page">
            <div className="free-session-container">
                <div className="free-session-card">
                    <header className="free-session-header">
                        <img src={logoImg} alt="Code Wave Logo" className="auth-logo-img" />
                        <h1 className="free-session-title">
                            <Sparkles className="title-icon" />
                            احجز سيشن مجانية الآن!
                        </h1>
                        <p className="free-session-subtitle">
                            ابدأ رحلتك في عالم البرمجة مع خبرائنا
                        </p>
                    </header>

                    {/* Features Section */}
                    <div className="features-grid">
                        <div className="feature-item">
                            <div className="feature-icon">
                                <Video size={24} />
                            </div>
                            <div className="feature-content">
                                <h3>سيشن برايفت 1:1</h3>
                                <p>جلسة خاصة مع مدرب محترف</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Users size={24} />
                            </div>
                            <div className="feature-content">
                                <h3>محاضرات تفاعلية</h3>
                                <p>تعلم بطريقة ممتعة وعملية</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Award size={24} />
                            </div>
                            <div className="feature-content">
                                <h3>محتوى احترافي</h3>
                                <p>مناهج مصممة خصيصاً لك</p>
                            </div>
                        </div>

                        <div className="feature-item">
                            <div className="feature-icon">
                                <Clock size={24} />
                            </div>
                            <div className="feature-content">
                                <h3>مرونة كاملة</h3>
                                <p>اختر الوقت المناسب لك</p>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <Alert variant="error" className="mb-6">
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="free-session-form">
                        <div className="form-group">
                            <label className="form-label">
                                <User size={18} />
                                الاسم بالكامل
                                <span className="required">*</span>
                            </label>
                            <Input
                                type="text"
                                icon={<User size={18} />}
                                placeholder="اسمك الكامل"
                                value={formData.fullName}
                                onChange={(e) => handleInputChange('fullName', e.target.value)}
                                disabled={loading}
                                required
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">
                                    <Calendar size={18} />
                                    العمر
                                    <span className="required">*</span>
                                </label>
                                <Input
                                    type="number"
                                    icon={<Calendar size={18} />}
                                    placeholder="عمرك"
                                    min="6"
                                    max="100"
                                    value={formData.age}
                                    onChange={(e) => handleInputChange('age', e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    <Phone size={18} />
                                    رقم الهاتف
                                    <span className="required">*</span>
                                </label>
                                <Input
                                    type="tel"
                                    icon={<Phone size={18} />}
                                    placeholder="01xxxxxxxxx"
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <Mail size={18} />
                                البريد الإلكتروني
                                <span className="required">*</span>
                            </label>
                            <Input
                                type="email"
                                icon={<Mail size={18} />}
                                placeholder="name@example.com"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                disabled={loading}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <Clock size={18} />
                                الوقت المفضل للسيشن (اختياري)
                            </label>
                            <select
                                className="form-select"
                                value={formData.preferredTime}
                                onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                                disabled={loading}
                            >
                                <option value="">اختر الوقت المناسب</option>
                                <option value="morning">صباحاً (9 ص - 12 م)</option>
                                <option value="afternoon">ظهراً (12 م - 4 م)</option>
                                <option value="evening">مساءً (4 م - 8 م)</option>
                                <option value="night">ليلاً (8 م - 11 م)</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                <Award size={18} />
                                ما هي أهدافك من تعلم البرمجة؟ (اختياري)
                            </label>
                            <textarea
                                className="form-textarea"
                                placeholder="مثال: أريد تعلم تطوير المواقع لعمل مشروعي الخاص..."
                                rows="2"
                                value={formData.goals}
                                onChange={(e) => handleInputChange('goals', e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <ButtonPrimary
                            type="submit"
                            className="auth-btn free-session-btn"
                            disabled={loading}
                            size="lg"
                        >
                            {loading ? (
                                <Spinner size="sm" />
                            ) : (
                                <>
                                    <Sparkles size={20} className="mr-2" />
                                    احجز السيشن المجانية الآن
                                    <Sparkles size={20} className="ml-2" />
                                </>
                            )}
                        </ButtonPrimary>

                        <div className="form-note">
                            <CheckCircle2 size={16} />
                            سيتم التواصل معك خلال 24 ساعة لتأكيد الموعد
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
